import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateCreditApplication } from '@src/data/api/credit-application/credit-application-api'
import { useIsRefinancing } from '@src/data/selectors/credit-selectors'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb, PageSpinner } from '../../components'
import { CreditApplication } from '../../data/types'
import { reportErrorToServer } from '../../services/error-logger'
import CreditApplicationForm from './components/CreditApplicationForm/CreditApplicationForm'

const EditCreditApplicationPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [updateCreditApplication, isUpdating] = useUpdateCreditApplication()
  const navigate = useNavigate()
  const { creditApplication, isLoading } = useCreditApplicationData()
  const isRefinancing = useIsRefinancing()
  const editDisabled = creditApplication?.editLocked || isRefinancing

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${creditApplication?.financingProgramId}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('credit.edit') },
  ]

  const onSave = (data: Partial<CreditApplication>) => {
    const updatedData = {
      ...data,
      applicant: {
        ...data.applicant,
        expenses: { ...data.applicant?.expenses, isLaw134Confirmed: false },
      },
      coApplicant: data.coApplicant
        ? {
            ...data.coApplicant,
            expenses: { ...data.coApplicant?.expenses, isLaw134Confirmed: false },
          }
        : null,
    }
    updateCreditApplication(updatedData as Partial<CreditApplication>)
      .then((creditApp) => {
        navigate(`/Applications/${creditApp.financingProgramId}/${creditApp.id}/view`, { replace: true })
      })
      .catch(reportErrorToServer)
  }

  const getEditLockMessage = () => {
    if (isRefinancing) {
      return t('editCreditApplication.errors.creditApplicationLockedRefinancing')
    }
    return t('editCreditApplication.errors.creditApplicationLocked')
  }

  return (
    <>
      <PageSpinner isLoading={isLoading || isUpdating} withBackdrop={isUpdating} />
      {!isLoading && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          {editDisabled && <h1>{getEditLockMessage()}</h1>}
          <CreditApplicationForm creditApplication={creditApplication} onSave={onSave} />
        </>
      )}
    </>
  )
}

export default EditCreditApplicationPage
